import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  useMediaQuery,
} from '@mui/material';
import Swal from 'sweetalert2';
import api from '../../adapters/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import 'leaflet-control-geocoder';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';

let DefaultIcon = L.icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const MapWithClick = ({ onClick }) => {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
};

const MapWithSearch = ({ onSelect }) => {
  const map = useMap();
  useEffect(() => {
    const geocoder = new L.Control.Geocoder({ defaultMarkGeocode: false });
    geocoder.on('markgeocode', ev => {
      onSelect(ev.geocode.center);
      map.setView(ev.geocode.center, 15);
    });
    geocoder.addTo(map);
    return () => map.removeControl(geocoder);
  }, [map, onSelect]);
  return null;
};

const UpdateMapCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const isValidLatLng = (lat, lng) => {
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  return !isNaN(latNum) && !isNaN(lngNum) && latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180;
};

const sameName = (a, b) => (a || '').trim() === (b || '').trim();

// ตัด " ต.X อ.Y จ.Z 12345" ท้ายที่อยู่ออก (ใช้ตอนเปลี่ยน ต./อ./จ. จาก dropdown)
const stripLocationSuffix = (text) =>
  (text || '').replace(/(^|\s)ต\..*$/, '').replace(/\s*\d{5}$/, '').trim();

export default function NewDeliveryLocationDialog({ open, onClose, onSuccess, customerId }) {
  // มือถือ/แท็บเล็ต: เต็มจอ แผนที่อยู่บน ฟอร์มอยู่ล่าง (แบบซ้าย-ขวาสูง 520px ล้นจอ และฟอร์มแคบเกิน)
  const isCompact = useMediaQuery(theme => theme.breakpoints.down('md'));
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [address, setAddress] = useState('');
  const [subdistrict, setSubdistrict] = useState('');
  const [district, setDistrict] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [provinceList, setProvinceList] = useState([]);
  const [amperList, setAmperList] = useState([]);
  const [tambonList, setTambonList] = useState([]);

  useEffect(() => {
    if (!open || provinceList.length > 0) return;
    Promise.all([
      api.post('/api/getProvinceList'),
      api.post('/api/getAmperList'),
      api.post('/api/getTambonList'),
    ])
      .then(([prov, amper, tambon]) => {
        setProvinceList(prov.data);
        setAmperList(amper.data);
        setTambonList(tambon.data);
      })
      .catch(err => console.error('Error loading province/amper/tambon lists:', err));
  }, [open, provinceList.length]);

  // state เก็บเป็น "ชื่อ" (backend หา code จากชื่อเอง) — จับคู่กลับเป็น object เพื่อแสดงใน Autocomplete
  const selectedProvince = useMemo(
    () => provinceList.find(p => sameName(p.name_1, province)) || null,
    [provinceList, province]
  );
  const selectedAmper = useMemo(
    () => amperList.find(a => a.province === selectedProvince?.code && sameName(a.name_1, district)) || null,
    [amperList, selectedProvince, district]
  );
  const selectedTambon = useMemo(
    () => tambonList.find(t => t.amper === selectedAmper?.code && sameName(t.name_1, subdistrict)) || null,
    [tambonList, selectedAmper, subdistrict]
  );

  const handleProvinceChange = (_, value) => {
    setProvince(value?.name_1?.trim() || '');
    setDistrict('');
    setSubdistrict('');
    setPostalCode('');
    setAddress(prev => stripLocationSuffix(prev));
    setErrors(prev => ({ ...prev, province: false }));
  };

  const handleAmperChange = (_, value) => {
    setDistrict(value?.name_1?.trim() || '');
    setSubdistrict('');
    setPostalCode('');
    setAddress(prev => stripLocationSuffix(prev));
    setErrors(prev => ({ ...prev, district: false }));
  };

  const handleTambonChange = (_, value) => {
    const tName = value?.name_1?.trim() || '';
    const zip = value?.zip_code || '';
    setSubdistrict(tName);
    setPostalCode(zip);
    // ต่อท้ายที่อยู่ด้วย ต./อ./จ. เหมือนที่อยู่ที่ได้จากแผนที่ (ใช้ทำ remark ใบเสนอราคา)
    setAddress(prev => {
      const manual = stripLocationSuffix(prev);
      if (!tName) return manual;
      return [manual, `ต.${tName} อ.${district} จ.${province}`, zip].filter(Boolean).join(' ');
    });
    setErrors(prev => ({ ...prev, subdistrict: false, postalCode: false }));
  };

  const resetForm = () => {
    setLatitude('');
    setLongitude('');
    setAddress('');
    setSubdistrict('');
    setDistrict('');
    setProvince('');
    setPostalCode('');
    setRecipientName('');
    setContactNumber('');
    setErrors({});
  };

  const fetchAddress = useCallback(async ({ lat, lng }) => {
    setLatitude(lat);
    setLongitude(lng);
    try {
      const res = await api.get(`/api/getAddress?lat=${lat}&lng=${lng}`);
      const data = res.data;
      setAddress(data.address);
      setSubdistrict(data.subdistrict);
      setDistrict(data.district);
      setProvince(data.province);
      setPostalCode(data.postal_code);
    } catch {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถดึงที่อยู่ได้' });
    }
  }, []);

  const handleLatChange = e => setLatitude(e.target.value);
  const handleLngChange = e => setLongitude(e.target.value);

  const handleCoordPaste = useCallback((e) => {
    const paste = e.clipboardData.getData('Text').trim();
    if (paste.includes(',')) {
      const [latStr, lngStr] = paste.split(',').map(s => s.trim());
      if (isValidLatLng(latStr, lngStr)) {
        e.preventDefault();
        setLatitude(latStr);
        setLongitude(lngStr);
      } else {
        Swal.fire({ icon: 'error', title: 'พิกัดไม่ถูกต้อง', text: 'รูปแบบพิกัดที่วางมาไม่ถูกต้อง กรุณาใช้รูปแบบ lat, lng' });
      }
    }
  }, []);

  const center = isValidLatLng(latitude, longitude)
    ? [parseFloat(latitude), parseFloat(longitude)]
    : [18.788567, 98.985542];

  // ไม่ล้างที่อยู่เมื่อพิกัดว่าง — ผู้ใช้อาจเลือก ต./อ./จ. เองโดยไม่มีพิกัด
  useEffect(() => {
    if (isValidLatLng(latitude, longitude)) {
      fetchAddress({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
    }
  }, [latitude, longitude, fetchAddress]);

  const handleSave = async () => {
    // พิกัดไม่บังคับ แต่ถ้ากรอกต้องถูกต้องทั้งคู่
    const hasCoords = Boolean(latitude || longitude);
    const coordsInvalid = hasCoords && !isValidLatLng(latitude, longitude);
    const newErr = {
      recipientName: !recipientName,
      contactNumber: !/^[0-9]{9,10}$/.test(contactNumber),
      latitude: coordsInvalid,
      longitude: coordsInvalid,
      address: !address,
      // ต้องตรงกับรายการใน erp (backend หา code จากชื่อ) — ชื่อจากแผนที่ที่ไม่ตรงจะต้องเลือกเอง
      subdistrict: !selectedTambon,
      district: !selectedAmper,
      province: !selectedProvince,
      postalCode: !postalCode,
    };
    setErrors(newErr);
    if (Object.values(newErr).some(Boolean)) {
      Swal.fire({ icon: 'warning', title: 'กรุณาตรวจสอบข้อมูล', text: 'กรุณากรอกข้อมูลให้ครบถ้วนก่อนบันทึก' });
      return;
    }
    try {
      const payload = {
        customerId, recipientName, contactNumber,
        latitude: hasCoords ? latitude : null,
        longitude: hasCoords ? longitude : null,
        address, subdistrict, district, province, postalCode,
      };
      const resp = await api.post('/api/addDeliveryLocation', payload);
      if (resp.status >= 200 && resp.status < 300) {
        onSuccess(resp.data.insertId);
        Swal.fire({ icon: 'success', title: 'บันทึกสำเร็จ', text: 'เพิ่มที่อยู่ใหม่เรียบร้อยแล้ว', confirmButtonColor: '#2e7d32' });
        resetForm();
        onClose();
      }
    } catch (err) {
      console.error('Error saving location:', err);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกที่อยู่ได้ กรุณาลองใหม่อีกครั้ง' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isCompact}>
      <DialogTitle sx={{ pb: 1 }}>เพิ่มสถานที่ส่งใหม่</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 520 } }}>

          {/* แผนที่ */}
          <Box sx={{
            flex: { xs: '0 0 auto', md: '0 0 60%' }, height: { xs: 280, md: 'auto' },
            borderRight: { md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider',
          }}>
            <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <UpdateMapCenter center={center} />
              <MapWithClick onClick={fetchAddress} />
              <MapWithSearch onSelect={fetchAddress} />
              {isValidLatLng(latitude, longitude) && (
                <Marker position={[parseFloat(latitude), parseFloat(longitude)]}>
                  <Popup>{address || 'ตำแหน่งที่เลือก'}</Popup>
                </Marker>
              )}
            </MapContainer>
          </Box>

          {/* แบบฟอร์ม */}
          <Box sx={{ flex: 1, overflowY: { md: 'auto' }, p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* ข้อมูลผู้รับ */}
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
              ข้อมูลผู้รับ
            </Typography>
            <Divider sx={{ mt: -0.5 }} />
            <TextField
              label="ชื่อผู้รับ"
              size="small"
              fullWidth
              value={recipientName}
              onChange={e => setRecipientName(e.target.value)}
              error={errors.recipientName}
              helperText={errors.recipientName ? 'กรุณาระบุชื่อผู้รับ' : ''}
            />
            <TextField
              label="เบอร์โทร"
              inputProps={{ inputMode: 'tel' }}
              size="small"
              fullWidth
              value={contactNumber}
              onChange={e => setContactNumber(e.target.value)}
              error={errors.contactNumber}
              helperText={errors.contactNumber ? 'กรุณาระบุเบอร์โทร 9-10 หลัก' : ''}
            />

            {/* พิกัด */}
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mt: 0.5 }}>
              พิกัดสถานที่
            </Typography>
            <Divider sx={{ mt: -0.5 }} />
            <Typography variant="caption" color="text.secondary">
              คลิกบนแผนที่ หรือวางพิกัดรูปแบบ "lat, lng" ในช่องละติจูด
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              ไม่บังคับ — ถ้ายังไม่มีพิกัด ให้เลือก ต./อ./จ. ด้านล่าง ระบบจะประมาณค่าขนส่งจากกลางตำบล
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                label="ละติจูด"
                inputProps={{ inputMode: 'decimal' }}
                size="small"
                fullWidth
                value={latitude}
                onChange={handleLatChange}
                onPaste={handleCoordPaste}
                error={errors.latitude}
                helperText={errors.latitude ? 'พิกัดไม่ถูกต้อง' : ''}
              />
              <TextField
                label="ลองจิจูด"
                inputProps={{ inputMode: 'decimal' }}
                size="small"
                fullWidth
                value={longitude}
                onChange={handleLngChange}
                error={errors.longitude}
                helperText={errors.longitude ? 'พิกัดไม่ถูกต้อง' : ''}
              />
            </Box>

            {/* ที่อยู่ */}
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mt: 0.5 }}>
              ที่อยู่
            </Typography>
            <Divider sx={{ mt: -0.5 }} />
            <TextField
              label="ที่อยู่"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={address}
              onChange={e => setAddress(e.target.value)}
              error={errors.address}
              helperText={errors.address ? 'กรุณาระบุที่อยู่' : ''}
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
              <Autocomplete
                size="small"
                fullWidth
                value={selectedProvince}
                options={provinceList.filter(Boolean)}
                getOptionLabel={o => o?.name_1 ?? ''}
                isOptionEqualToValue={(o, v) => o?.code === v?.code}
                onChange={handleProvinceChange}
                renderInput={p => (
                  <TextField {...p} label="จังหวัด" error={errors.province} helperText={errors.province ? 'จำเป็น' : ''} />
                )}
              />
              <Autocomplete
                size="small"
                fullWidth
                value={selectedAmper}
                disabled={!selectedProvince}
                options={amperList.filter(a => a && a.province === selectedProvince?.code)}
                getOptionLabel={o => o?.name_1 ?? ''}
                isOptionEqualToValue={(o, v) => o?.code === v?.code}
                onChange={handleAmperChange}
                renderInput={p => (
                  <TextField {...p} label="อำเภอ" error={errors.district} helperText={errors.district ? 'จำเป็น' : ''} />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
              <Autocomplete
                size="small"
                fullWidth
                value={selectedTambon}
                disabled={!selectedAmper}
                options={tambonList.filter(t => t && t.amper === selectedAmper?.code)}
                getOptionLabel={o => o?.name_1 ?? ''}
                isOptionEqualToValue={(o, v) => o?.code === v?.code}
                onChange={handleTambonChange}
                renderInput={p => (
                  <TextField {...p} label="ตำบล" error={errors.subdistrict} helperText={errors.subdistrict ? 'จำเป็น' : ''} />
                )}
              />
              <TextField label="รหัสไปรษณีย์" size="small" fullWidth value={postalCode} inputProps={{ inputMode: 'numeric' }}
                onChange={e => setPostalCode(e.target.value)}
                error={errors.postalCode} helperText={errors.postalCode ? 'จำเป็น' : ''} />
            </Box>

          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">ยกเลิก</Button>
        <Button onClick={handleSave} variant="contained">บันทึก</Button>
      </DialogActions>
    </Dialog>
  );
}
